import { type ReactNode, createContext, useContext } from "react";
import {
  Badge,
  Box,
  Column,
  InformationIcon,
  Row,
  Text,
  TextWithTooltip,
  Tooltip,
} from "@hightouch/htui";
import { OrderBy } from "src/graphql";
import { Table as UiTable, type TableColumn } from "src/ui/table/table";
import {
  PINNED,
  WIDTHS,
} from "src/pages/ad-studio/insights/performance/view/constants";
import { EntityCardOpen } from "src/pages/ad-studio/insights/performance/view/entity-card-open";
import { MediaPreview } from "src/pages/ad-studio/insights/performance/view/media-preview";
import { ListPager } from "src/pages/ad-studio/insights/performance/view/list-pager";
import { TableFrame } from "src/pages/ad-studio/insights/performance/view/table-frame";
import type {
  CellState,
  ColumnState,
  DrilledDown,
  EmptyText,
  ListColumn,
  Paged,
  PagingState,
  SortedColumn,
  SortState,
  TextTone,
} from "src/pages/ad-studio/insights/performance/view/types";

type PerformanceViewContextValue = {
  paging: PagingState;
  performAction: (action: Paged) => void;
};

const PerformanceViewContext = createContext<
  PerformanceViewContextValue | undefined
>(undefined);

const usePerformanceViewContext = (part: string) => {
  const context = useContext(PerformanceViewContext);
  if (context === undefined) {
    throw new Error(
      `\`PerformanceViewPrimitive.${part}\` must be used within \`PerformanceViewPrimitive.Root\``,
    );
  }
  return context;
};

type RootProps = PerformanceViewContextValue & { children?: ReactNode };

const Root = ({ paging, performAction, children }: RootProps) => (
  <PerformanceViewContext.Provider value={{ paging, performAction }}>
    {children}
  </PerformanceViewContext.Provider>
);

const TableLayout = ({ children }: { children?: ReactNode }) => {
  usePerformanceViewContext("TableLayout");
  return <TableFrame>{children}</TableFrame>;
};

const Pagination = () => {
  const { paging, performAction } = usePerformanceViewContext("Pagination");
  return <ListPager paging={paging} performAction={performAction} />;
};

const TONE_COLORS: Record<TextTone, string> = {
  primary: "text.primary",
  secondary: "text.secondary",
  tertiary: "text.tertiary",
  danger: "danger.base",
  warning: "warning.base",
  success: "success.base",
  info: "blue.base",
};

const SORT_DIRECTIONS: Record<SortState, OrderBy | null> = {
  ascending: OrderBy.Asc,
  descending: OrderBy.Desc,
  unsorted: null,
};

const Cell = ({ cell }: { cell: CellState }) => {
  switch (cell.kind) {
    case "entity-name": {
      return <EntityName name={cell.name} details={cell.details} />;
    }
    case "previewed-name": {
      return (
        <Row align="center" gap={2} minWidth={0}>
          <MediaPreview preview={cell.preview} boxSize={8} />
          <EntityName name={cell.name} details={cell.details} />
        </Row>
      );
    }
    case "text": {
      return (
        <TextWithTooltip size="sm" color={TONE_COLORS[cell.tone]}>
          {cell.text}
        </TextWithTooltip>
      );
    }
    case "number": {
      return (
        <Text variant="monospace" size="sm">
          {cell.text}
        </Text>
      );
    }
    case "badges": {
      return (
        <Row gap={1} flexWrap="wrap">
          {cell.badges.map((badge) => (
            <Badge key={badge.label} variant={badge.variant} size="sm">
              {badge.label}
            </Badge>
          ))}
        </Row>
      );
    }
    default: {
      return cell satisfies never;
    }
  }
};

const EntityName = ({
  name,
  details,
}: {
  name: string;
  details: readonly string[];
}) => (
  <Column minWidth={0} gap={0}>
    <TextWithTooltip fontWeight="medium" size="sm">
      {name}
    </TextWithTooltip>
    {details.map((detail) => (
      <Text key={detail} size="sm" color="text.tertiary">
        {detail}
      </Text>
    ))}
  </Column>
);

const InfoHeader = ({ label, info }: { label: string; info: string }) => (
  <Row align="center" gap={1}>
    <Text size="sm" fontWeight="medium" color="text.secondary">
      {label}
    </Text>
    <Tooltip message={info}>
      <Box display="flex" flexShrink={0}>
        <InformationIcon color="text.tertiary" />
      </Box>
    </Tooltip>
  </Row>
);

const toTableColumn = <Row extends Record<string, unknown>>({
  column,
  performAction,
}: {
  column: ListColumn<Row>;
  performAction: (action: SortedColumn) => void;
}): TableColumn<Row> => {
  const { state } = column;
  const width = WIDTHS[state.width];
  const pinned = state.pinned === "pinned-left";
  const { info } = state;
  const label =
    info.kind === "info"
      ? { header: () => <InfoHeader label={state.label} info={info.text} /> }
      : { name: state.label };
  return {
    ...label,
    min: width.min,
    ...(width.max === "no-max" ? {} : { max: width.max }),
    ...(pinned
      ? { headerSx: { ...PINNED, zIndex: 2 }, cellSx: { ...PINNED, zIndex: 1 } }
      : {}),
    ...sortPropsOf({ sort: state.sort, performAction }),
    cell: (row) => <Cell cell={column.cell(row)} />,
  };
};

const sortPropsOf = ({
  sort,
  performAction,
}: {
  sort: ColumnState["sort"];
  performAction: (action: SortedColumn) => void;
}) => {
  switch (sort.kind) {
    case "sortable": {
      return {
        sortDirection: SORT_DIRECTIONS[sort.state],
        onClick: () =>
          performAction({
            kind: "PerformanceViewAction/sorted-column",
            key: sort.key,
          }),
      };
    }
    case "not-sortable": {
      return {};
    }
    default: {
      return sort satisfies never;
    }
  }
};

type TableProps<Row> = {
  rows: readonly Row[];
  columns: readonly ListColumn<Row>[];
  primaryKey: keyof Row;
  empty: EmptyText;
};

/** A table whose rows only show their cells; its headers still sort. */
const Table = <Row extends Record<string, unknown>>({
  rows,
  columns,
  primaryKey,
  empty,
  performAction,
}: TableProps<Row> & { performAction: (action: SortedColumn) => void }) => (
  <UiTable<Row>
    columns={columns.map((column) => toTableColumn({ column, performAction }))}
    data={[...rows]}
    primaryKey={primaryKey}
    sortingEnabled
    scrollableX
    // Columns keep their natural width so a wide metric set scrolls rather than compressing every column toward zero.
    defaultMin="min-content"
    placeholder={{ title: empty.title, body: empty.message }}
  />
);

/** A table whose rows drill down into their entity or group when clicked. */
const DrilldownTable = <Row extends Record<string, unknown>>({
  rows,
  columns,
  primaryKey,
  empty,
  performAction,
}: TableProps<Row> & {
  performAction: (action: SortedColumn | DrilledDown<Row>) => void;
}) => (
  <UiTable<Row>
    columns={columns.map((column) => toTableColumn({ column, performAction }))}
    data={[...rows]}
    primaryKey={primaryKey}
    sortingEnabled
    scrollableX
    defaultMin="min-content"
    onRowClick={(row) =>
      performAction({ kind: "PerformanceViewAction/drilled-down", row })
    }
    placeholder={{ title: empty.title, body: empty.message }}
  />
);

/** Wraps any card so that clicking it, or its keyboard button, drills down. */
const DrilldownCard = ({
  name,
  onClick,
  children,
}: {
  name: string;
  onClick: () => void;
  children?: ReactNode;
}) => (
  <Box
    position="relative"
    display="flex"
    flexDirection="column"
    cursor="pointer"
    data-group
    onClick={onClick}
  >
    <EntityCardOpen name={name} />
    {children}
  </Box>
);

export const PerformanceViewPrimitive = {
  Root,
  TableLayout,
  Pagination,
  Table,
  DrilldownTable,
  DrilldownCard,
};
